import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { AdminPage, Button, EmptyState, ErrorState, Panel, Skeleton } from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import { RequestCard } from '../RequestsPage';
import { useRequestMutations, useRequests } from '../api';

/**
 * אישור בקשות (PRD 5.10.3).
 *
 * אישור של חופשה גם רושם אילוץ בסידור, ולכן ההודעה ליד הכפתור אומרת
 * את זה: המנהל צריך לדעת מה עוד קורה מהלחיצה שלו.
 */
export function RequestsAdminPage() {
  const canManage = usePerm('hr.manage');
  const requests = useRequests();
  const { decide } = useRequestMutations();
  const [note, setNote] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const all = requests.data ?? [];
  const pending = all.filter((request) => request.status === 'pending');
  const handled = all.filter((request) => request.status !== 'pending');

  return (
    // המסך יושב תחת עובדים ואין לו פריט תפריט משלו, ולכן הדרך
    // חזרה היא כאן — כמו בהרשמה ב QR ובייבוא
    <AdminPage title="בקשות" subtitle="חופשה, מחלה ובקשות כלליות" backTo="/admin/employees">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {requests.isPending && <Skeleton className="h-40" />}
      {requests.isError && <ErrorState onRetry={() => void requests.refetch()} />}

      {all.length === 0 && (
        <EmptyState title="אין בקשות" description="בקשות שעובדים ישלחו יופיעו כאן." />
      )}

      {pending.length > 0 && (
        <Panel title={`ממתינות (${pending.length})`}>
          <div className="grid gap-3">
            {pending.map((request) => (
              <RequestCard key={request.id} request={request} showName>
                <div className="grid gap-2">
                  <input
                    value={note[request.id] ?? ''}
                    onChange={(event) => setNote({ ...note, [request.id]: event.target.value })}
                    placeholder="הערה לעובד/ת, לא חובה"
                    aria-label="הערה לעובד/ת"
                    className="min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      onClick={() =>
                        run(() =>
                          decide.mutateAsync({
                            id: request.id,
                            approve: true,
                            note: note[request.id],
                          }),
                        )
                      }
                    >
                      <Check size={20} strokeWidth={1.75} aria-hidden="true" />
                      אישור
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        run(() =>
                          decide.mutateAsync({
                            id: request.id,
                            approve: false,
                            note: note[request.id],
                          }),
                        )
                      }
                    >
                      <X size={20} strokeWidth={1.75} aria-hidden="true" />
                      דחייה
                    </Button>
                  </div>
                </div>
              </RequestCard>
            ))}
          </div>
        </Panel>
      )}

      {handled.length > 0 && (
        <Panel title="טופלו">
          <div className="grid gap-3">
            {handled.slice(0, 30).map((request) => (
              <RequestCard key={request.id} request={request} showName />
            ))}
          </div>
        </Panel>
      )}
    </AdminPage>
  );
}
